// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    priceAndActions?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    priceAvailabilityLink?: InjectableCss;
    priceAvailabilityContainer?: InjectableCss;
    addToCartContainer?: InjectableCss;
}

export const productGridContainerStyle: ProductGridContainerStyle = {
    container: {
        css: css`
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            max-width: 320px;
            width: 100%;
            border: 1px solid ${color("common.ui2")};
            border-radius: 4px;
            background-color: transparent;
            box-shadow: none;
            box-sizing: border-box;
            padding: 8px;
            ${xScreen("lte", "md")} {
                border: initial;
                padding: 0;
            }
            ${xScreen("lte", "sm")} {
                margin: 0 auto;
            }
            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }

            & > .top-section {
                margin-bottom: 8px;
                ${xScreen("lte", "md")} {
                    margin-bottom: 0;
                }
            }
            & > .middle-section {
                margin-bottom: 8px;
                ${xScreen("lte", "md")} {
                    height: 100%;

                    .product-grid-container-info {
                        height: 100%;
                    }
                }
            }
            & > .bottom-section {
                display: flex;
                flex-direction: column;
                gap: 12px;
                height: 100%;
                ${xScreen("lte", "md")} {
                    height: auto;
                }
            }
        `,
    },
    image: {
        css: css`
            position: relative;
            width: 300px;
            height: 300px;
            margin: 0 auto;
            border-radius: 4px;
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    price: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    availability: {
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
    addToCartActions: {
        css: css``,
    },
    addToList: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    priceAndActions: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0;
            gap: 8px;
            height: 100%;
        `,
    },
};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});
const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Info = "Info",
    AddToCartActions = "AddToCartActions",
    Price = "Price",
    Availability = "Availability",
    AddToList = "AddToList",
    PriceAndActions = "PriceAndActions",
    AutoDeliveryButton = "AutoDeliveryButton",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AddToList),
        getChild(childrenArray, NAMES.PriceAndActions),
        getChild(childrenArray, NAMES.AutoDeliveryButton),
    ];
};

const ProductGridContainer = (props: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const { children, extendedStyles, ...otherProps } = props;
    const [styles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, info, addToCartActions, price, availability, addToList, PriceAndActions, AutoDeliveryButton] =
        React.useMemo(() => getDestructeredChildren(childrenArray), [childrenArray]);
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps} className={sharedClassName}>
                <StyledWrapper className="top-section">{image}</StyledWrapper>
                <StyledWrapper className="middle-section">{info}</StyledWrapper>
                <StyledWrapper className="bottom-section">
                    {(price || availability) && (
                        <StyledWrapper {...styles.priceAvailabilityContainer} className="price-availability-container">
                            {price}
                            {availability}
                        </StyledWrapper>
                    )}
                    {(!!addToCartActions || !!AutoDeliveryButton) && (
                        <StyledWrapper {...styles.addToCartContainer}>
                            {addToCartActions}
                            {/* in case we have this button the gap will be different */}
                            {AutoDeliveryButton}
                        </StyledWrapper>
                    )}
                    {addToList}
                    {PriceAndActions}
                </StyledWrapper>
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} data-test-selector="productCarousel-AddToListActions">
            {children}
        </StyledWrapper>
    );
};

const FragmentComponent: FC = ({ children }) => <>{children}</>;

const PriceAndActions: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceAndActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...priceAndActions} className="product-grid-container-priceAndActions">
            {children}
        </StyledWrapper>
    );
};

const Price: FC = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;
ProductGridContainer.Info = Info;
ProductGridContainer.Info.displayName = NAMES.Info;
ProductGridContainer.PriceAndActions = PriceAndActions;
ProductGridContainer.PriceAndActions.displayName = NAMES.PriceAndActions;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;
ProductGridContainer.AutoDeliveryButton = FragmentComponent;
ProductGridContainer.AutoDeliveryButton.displayName = NAMES.AutoDeliveryButton;

export default ProductGridContainer;
